import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Select, Switch, Tooltip, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';
import './AsanaInstall.less';
import { isAxiosError } from 'axios';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';
import { ErrorPage404, ErrorPage500 } from '@pages/ErrorPage';
import { availableIntegrations } from '@pages/AdminSettings/components/IntegrationTab/constants';
import { DiamondUpgradeIcon, InfoIcon, LockIcon } from '@icons';

const { Title } = Typography;

interface AsanaOAuthInstallProps {
  loader: ReactElement;
}

export default function AsanaOAuthInstall({ loader }: AsanaOAuthInstallProps) {
  const [searchParams] = useSearchParams();
  const client = searchParams.get('client');
  const asanaWorkspaceId = searchParams.get('domainId');
  const [step, setStep] = useState<'loading' | 'org' | 'options'>('loading');
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('');
  const [createReviewerSubtasks, setCreateReviewerSubtasks] =
    useState<boolean>(false);
  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const {
        data: { list }
      } =
        await apiClient.organization.organizationControllerListUserOrganizations();
      return list;
    }
  });
  const organizations = useMemo(
    () => organizationsQuery.data || [],
    [organizationsQuery.data]
  );

  const installMutation = useMutation({
    mutationFn: async ({
      organizationId,
      createReviewerSubtasks
    }: {
      organizationId: string;
      createReviewerSubtasks: boolean;
    }) => {
      const {
        data: { url, handle }
      } = await apiClient.asana.asanaControllerInstall({
        asanaWorkspaceId: nonNull(asanaWorkspaceId),
        organizationId,
        client: client || undefined,
        createReviewerSubtasks
      });

      if (!url || !handle) {
        return;
      }

      sessionStorage.setItem('asana-oauth-handle', handle);
      window.location.href = url;
    }
  });

  const nextStepMutation = useMutation({
    mutationFn: async (organizationId: string) => {
      const organization = nonNull(
        organizations.find((org) => org.id === organizationId)
      );
      const { tier } = availableIntegrations.asana;
      const needUpgrade =
        tier === 'advanced'
          ? !organization.features.advancedIntegrations
          : !organization.features.basicIntegrations;
      if (needUpgrade) {
        const error = new Error(
          `Organization ${organization.name} does not have the required plan to install the Asana integration. Please upgrade your plan to install this integration.`
        );
        (error as any).code = 'plan_upgrade_required';
        (error as any).plan = tier === 'advanced' ? 'an Enterprise' : 'a Team';
        throw error;
      }
      setSelectedOrganizationId(organizationId);
      let showOptionsStep = false;
      if (organization.permissions.installIntegrations) {
        const { data: isInstalled } =
          await apiClient.asana.asanaControllerCheckInstalled({
            organizationId
          });
        showOptionsStep = !isInstalled;
      }
      if (showOptionsStep) {
        setStep('options');
      } else {
        await installMutation.mutateAsync({
          organizationId,
          createReviewerSubtasks: false
        });
      }
    }
  });

  const defaultOrgId =
    organizations.length === 1 ? organizations[0].id : undefined;

  const isLoading =
    step === 'loading' ||
    organizationsQuery.isLoading ||
    installMutation.isPending ||
    installMutation.isSuccess;

  const isError =
    organizationsQuery.isError ||
    installMutation.isError ||
    nextStepMutation.isError;

  const nextStepMutationRef = useRef(nextStepMutation);
  nextStepMutationRef.current = nextStepMutation;
  useEffect(() => {
    if (defaultOrgId) {
      nextStepMutationRef.current.mutate(defaultOrgId);
    } else if (!organizationsQuery.isLoading) {
      setStep('org');
    }
  }, [defaultOrgId, organizationsQuery.isLoading]);

  if (!asanaWorkspaceId) {
    return <ErrorPage404 onGoBack={() => window.close()} />;
  }

  if (isError) {
    const error =
      organizationsQuery.error ??
      nextStepMutation.error ??
      installMutation.error;
    if (
      isAxiosError(error) &&
      error.response?.data.errorCode === 'organization_permission_denied'
    ) {
      return (
        <div className="asana-install">
          <div className="asana-install__content">
            <div className="asana-install__lock-icon">
              <LockIcon />
            </div>
            <Title level={2} className="asana-install__title">
              You don&apos;t have permissions to install integrations
            </Title>
            <Title level={5} className="asana-install__description">
              Contact your organization admin to install the Asana integration
            </Title>
          </div>
        </div>
      );
    }
    if (
      isAxiosError(error) &&
      error.response?.data.errorCode ===
        'organization_already_has_asana_integration'
    ) {
      return (
        <div className="asana-install">
          <div className="asana-install__content">
            <Title level={2} className="asana-install__title">
              This organization already has an Asana integration installed
            </Title>
            <Title level={5} className="asana-install__description">
              Contact{' '}
              <a href="mailto:support@streamwork.com">support@streamwork.com</a>{' '}
              if you believe this is a mistake
            </Title>
          </div>
        </div>
      );
    }
    if ((error as any)?.code === 'plan_upgrade_required') {
      return (
        <div className="asana-install">
          <div className="asana-install__content">
            <Title level={2} className="asana-install__title">
              <DiamondUpgradeIcon className="upgrade-icon" /> Upgrade to{' '}
              {(error as any)?.plan || 'a higher'} plan to install the Asana
              integration
            </Title>
          </div>
        </div>
      );
    }
    return <ErrorPage500 onGoBack={() => window.close()} />;
  }

  if (isLoading) {
    return loader;
  }

  if (step === 'org') {
    return (
      <div className="asana-install">
        <div className="asana-install__content">
          <Title level={2} className="asana-install__title">
            Select Organization
          </Title>
          <Title level={5} className="asana-install__description">
            Choose an organization to connect with Asana workspace
          </Title>
          <Select
            className="asana-install__select"
            size="large"
            loading={organizationsQuery.isLoading}
            placeholder="Select an organization..."
            onChange={setSelectedOrganizationId}
            options={organizations.map((org) => ({
              label: org.name,
              value: org.id
            }))}
          />
          <Button
            className="asana-install__button"
            type="primary"
            disabled={!selectedOrganizationId}
            loading={nextStepMutation.isPending}
            onClick={() => nextStepMutation.mutate(selectedOrganizationId)}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="asana-install">
      <div className="asana-install__content">
        <Title level={2} className="asana-install__title">
          Integration Options
        </Title>
        <Title level={5} className="asana-install__description">
          Configure how StreamWork integrates with your Asana workspace
        </Title>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12
          }}
        >
          <Switch
            checked={createReviewerSubtasks}
            onChange={setCreateReviewerSubtasks}
          />
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            Create a subtask for each reviewer in Asana
            <Tooltip
              overlayClassName="asana-install__tooltip"
              title="When an approval workflow is attached to an Asana task, StreamWork will automatically create a subtask for each reviewer, making it easy to track individual review progress directly in Asana."
            >
              <InfoIcon />
            </Tooltip>
          </span>
        </div>
        <Button
          className="asana-install__button"
          type="primary"
          loading={installMutation.isPending}
          onClick={() =>
            installMutation.mutate({
              organizationId: selectedOrganizationId,
              createReviewerSubtasks
            })
          }
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
